import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from 'lib/utils'

interface AspectRatioOption {
  value: string
  label: string
}

const aspectRatioOptions: AspectRatioOption[] = [
  { value: '16:9', label: '16:9 (가로형 - 일반 이미지)' },
  { value: '9:16', label: '9:16 (세로형 - 쇼츠/릴스)' },
  { value: '1:1', label: '1:1 (정사각형 - 인스타그램)' },
  { value: '3:4', label: '3:4 (세로형 포트레이트)' },
  { value: '4:3', label: '4:3 (가로형 전통)' },
  { value: '3:2', label: '3:2 (클래식 사진)' },
  { value: '2:3', label: '2:3 (세로형 사진)' },
  { value: '4:5', label: '4:5 (인스타그램 세로)' },
  { value: '5:4', label: '5:4 (인스타그램 가로)' },
  { value: '21:9', label: '21:9 (울트라와이드)' },
]

interface AspectRatioSelectorProps {
  value: string
  onChange: (value: string) => void
  className?: string
}

export function AspectRatioSelector({
  value,
  onChange,
  className,
}: AspectRatioSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const selectorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  const selectedOption = aspectRatioOptions.find(
    (option) => option.value === value,
  )

  return (
    <div ref={selectorRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-xl border border-zinc-700/50 bg-zinc-800/50 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-zinc-700/50"
      >
        <span>{selectedOption?.label || value}</span>
        <ChevronDown
          size={12}
          className={cn(
            'transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-40 rounded-xl border border-zinc-700/50 bg-zinc-800/90 py-1 shadow-xl backdrop-blur-sm">
          {aspectRatioOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'block w-full px-3 py-2 text-left text-xs font-medium transition-colors hover:bg-zinc-700/50',
                option.value === value
                  ? 'bg-zinc-700/50 text-main'
                  : 'text-white',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
